import { useState } from 'react'
import { createPortal } from 'react-dom'
import { CATEGORY_SETUP, useEditor } from '../store/useEditor'
import type { QuoteLines } from '../lib/quote'
import { quoteText } from '../lib/quoteText'
import { defaultPaper, MONTHS, MONTHS_SHORT } from '../lib/paper'
import { useSaid } from './bits'
import {
  balanceDue,
  depositAmount,
  dueWording,
  paidAmount,
  discount,
  discountLabel,
  docNumber,
  listCents,
  longDate,
  money,
  offFraction,
  priceCents,
  subtotal,
  total,
  totalAt,
} from '../lib/paper'

/**
 * THE DOCUMENT THE CUSTOMER SEES.
 *
 * Every number on this page is read, not entered. There is no editing here on
 * purpose: the estimate screen is where a person types, and this is what comes
 * out. Two surfaces that both take input would eventually disagree about which
 * one was the real quote.
 *
 * IT IS A PRINTED PAGE, NOT A WEB PAGE. Sized in inches, because it is going
 * to be a PDF, and a layout in pixels lands wherever the printer feels like.
 * Print to PDF from the browser and the file is exact, with no library in the
 * bundle and nothing sent anywhere.
 *
 * WHY THE STRUCK-THROUGH COLUMN MATTERS. The seasonal ladder is the reason a
 * customer signs in September instead of thinking about it until November, so
 * the discount is not a footnote at the bottom. It is on every line, and the
 * three months are priced along the bottom so the cost of waiting is a number
 * rather than a warning.
 *
 * A QUOTE AND AN INVOICE ARE THE SAME PAGE. The word in the corner changes,
 * the deposit appears, and the black box stops saying TOTAL and starts saying
 * BALANCE DUE. Nothing is renumbered and nothing is retyped.
 */

function Lines({ lines, off }: { lines: QuoteLines; off: number }) {
  return (
    <>
      {lines.map((l) => (
        <div className="qd-row" key={l.id}>
          <div className="qd-desc">
            <strong>{l.label?.trim() || CATEGORY_SETUP[l.category].label}</strong>
            {l.note.trim() && <span>{l.note}</span>}
          </div>
          <div className="qd-lit">{l.lighting}</div>
          <div className="qd-list">{off > 0 ? <s>{money(listCents(l) / 100)}</s> : ''}</div>
          <div className="qd-amt">{money(priceCents(l, off) / 100)}</div>
        </div>
      ))}
    </>
  )
}

export default function QuoteDoc({ onClose }: { onClose: () => void }) {
  const doc = useEditor((s) => s.doc)
  const company = useEditor((s) => s.company)
  const markSent = useEditor((s) => s.markSent)
  const { said, say } = useSaid()
  const lines = doc.quote ?? []
  const paper = doc.paper ?? defaultPaper()

  const off = offFraction(paper)
  const sub = subtotal(lines)
  const disc = discount(lines, paper)
  const tot = total(lines, paper)
  const dep = depositAmount(lines, paper)
  const bal = balanceDue(lines, paper)

  const invoice = paper.kind === 'invoice'
  /*
   * WHAT WAS RECEIVED, NOT WHAT WAS ASKED FOR.
   *
   * This read the deposit's own paid flag, which was set by ticking a box next
   * to a percentage - so setting a deposit and being handed one were the same
   * event, and the sheet could tell a homeowner they had already paid half a
   * job they had not paid a cent of. It reads the payment list now, and it
   * shows on a quote as well as an invoice: money that has arrived is money
   * that has arrived, whatever the header says.
   */
  const received = paidAmount(lines, paper)
  const paid = received > 0
  const monthWord = paper.installMonth == null ? '' : MONTHS[paper.installMonth]
  // The price column is an inch and a bit wide, so the month is abbreviated
  // there and spelled out everywhere it has room.
  const monthShort = paper.installMonth == null ? '' : MONTHS_SHORT[paper.installMonth]

  // Two lines of street, the way an envelope is addressed. A single line is
  // left alone rather than guessed at.
  const addr = doc.address
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)

  const contact = [company.address, company.phone].filter((x) => x.trim()).join('   ·   ')

  /*
   * Straight onto the body, not into the app tree. Printing then has one rule
   * to follow - hide every sibling - instead of guessing how deep the editor
   * nests this. The alternative, hiding by visibility, leaves the canvas
   * occupying its full height and prints two blank pages after the quote.
   */
  return createPortal(
    <div className="qd-wrap" role="dialog" aria-label="Quote document">
      <div className="qd-bar">
        <button className="btn" onClick={onClose}>
          Back
        </button>
        {/*
          SENDING IS WHAT MOVES A JOB ALONG.
          Not opening the screen and not saving the file: a quote is waiting on
          an answer from the moment it leaves, and these two buttons are the
          only ways it leaves. Marking it here means nobody has to remember to
          go and set a status afterward, which is the step everybody skips.
        */}
        <span className="qd-bar-note" role="status">
          {said ? said.text : 'Print, then choose Save as PDF'}
        </span>
        <button
          className="btn"
          onClick={async () => {
            const text = quoteText(company, doc, lines, paper)
            try {
              await navigator.clipboard.writeText(text)
              say('Copied. Paste it into a text or an email.')
            } catch {
              say('This browser would not let the app copy. Print instead.', 'bad')
            }
          }}
        >
          Copy as text
        </button>
        <button className="btn primary" onClick={() => window.print()}>
          Print / Save PDF
        </button>

        {doc.status === 'draft' && (
          <button
            className="btn"
            onClick={() => {
              markSent()
              say('Marked as sent. It is waiting on them now.')

            }}
            title="Move this job to waiting on the customer"
          >
            Mark as sent
          </button>
        )}
      </div>

      <div className="qd-sheet">
        <header className="qd-band">
          {company.logoDataUrl ? (
            <img src={company.logoDataUrl} alt={company.name} className="qd-logo" />
          ) : (
            <p className="qd-wordmark">{company.name || 'Your Company'}</p>
          )}
          {contact && <p className="qd-contact">{contact}</p>}
        </header>

        <div className="qd-body">
          <section className="qd-parties">
            <div>
              <p className="qd-eyebrow">Prepared for</p>
              <h1 className="qd-name">{doc.customer || doc.name}</h1>
              {addr.map((a, i) => (
                <p className="qd-line" key={i}>
                  {a}
                </p>
              ))}
              {paper.customerPhone && <p className="qd-line">{paper.customerPhone}</p>}
            </div>
            <div className="qd-right">
              <p className="qd-eyebrow">{invoice ? 'Invoice' : 'Quote'}</p>
              <h1 className="qd-name">{docNumber(paper, company.prefix) || '—'}</h1>
              <p className="qd-line">{longDate(paper.date)}</p>
              {monthWord && <p className="qd-line">{monthWord} install</p>}
            </div>
          </section>

          <section className="qd-table">
            <div className="qd-head">
              <div className="qd-desc">Description</div>
              <div className="qd-lit">Lighting</div>
              <div className="qd-list">{off > 0 ? 'List' : ''}</div>
              <div className="qd-amt">{off > 0 && monthShort ? `${monthShort} price` : 'Amount'}</div>
            </div>
            <Lines lines={lines} off={off} />
          </section>

          <section className="qd-sums">
            <div className="qd-sum">
              <span>Subtotal</span>
              <span>{money(sub)}</span>
            </div>
            {disc > 0 && (
              <div className="qd-sum qd-off">
                <span>{discountLabel(paper)}</span>
                <span>&minus;{money(disc)}</span>
              </div>
            )}
            {/*
              EVERY PAYMENT ON ITS OWN LINE, with the day it arrived. One
              lumped "deposit received" cannot describe a job paid in three
              pieces, and the homeowner checking this against their bank wants
              to find their own dates.
            */}
            {paper.payments.map((q) => (
              <div className="qd-sum qd-off" key={q.id}>
                <span>
                  Payment received {longDate(q.date)}
                  {q.method ? ` · ${q.method}` : ''}
                </span>
                <span>&minus;{money(q.amount)}</span>
              </div>
            ))}

            <div className="qd-total">
              <span className="qd-total-lbl">{paid ? 'Balance due' : invoice ? 'Amount due' : 'Total'}</span>
              <span className="qd-total-amt">{money(paid ? bal : tot)}</span>
            </div>

            {/*
              THE DEPOSIT LINE AND THE DUE LINE ARE TWO DIFFERENT CLAIMS.
              A deposit reserves a date; the balance is settled on completion
              or on a day the contractor named. Printing one date for both is
              how an invoice ends up demanding the whole job on delivery.
            */}
            {dep > 0 && received < dep && (
              <p className="qd-deposit-note">
                {money(dep)} deposit requested to reserve your install date.
              </p>
            )}
            {invoice && bal > 0 && <p className="qd-deposit-note">{dueWording(paper)}.</p>}
          </section>

          {paper.showLadder && paper.tiers.length > 0 && sub > 0 && (
            <section className="qd-ladder-wrap">
              <p className="qd-eyebrow">Install month pricing</p>
              <div className="qd-ladder">
                {paper.tiers.map((t) => (
                  <div
                    className={'qd-rung' + (t.month === paper.installMonth ? ' on' : '')}
                    key={t.month}
                  >
                    <p className="qd-rung-m">{MONTHS[t.month]}</p>
                    <p className="qd-rung-off">{t.offPct > 0 ? `${t.offPct}% off` : 'full price'}</p>
                    <p className="qd-rung-amt">{money(totalAt(lines, t.offPct))}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(paper.termsLeft.trim() || paper.termsRight.trim()) && (
            <section className="qd-terms-wrap">
              <p className="qd-eyebrow">Terms</p>
              <div className="qd-terms">
                <p>{paper.termsLeft}</p>
                <p>{paper.termsRight}</p>
              </div>
            </section>
          )}

          <p className="qd-thanks">Thank you for the opportunity to light up your property.</p>
        </div>

        {/*
          The gold rule that used to sit above a footer, kept without one. The
          footer repeated the letterhead a page below the letterhead, and the
          page still needs something to close on.
        */}
        <div className="qd-endrule" />
      </div>
    </div>,
    document.body,
  )
}
